<script lang="ts">
    // Bloco "Quando": data e início sempre visíveis; o término é calculado pela duração dos serviços
    // e só vira campo quando o atendente toca em "Ajustar" (é o que o antigo botão da calculadora
    // fazia — entity.auto). Voltar para "Automático" recalcula pelo serviço.
    import { createEventDispatcher } from 'svelte'
    import dayjs from 'dayjs'
    import type { Appointment } from '../../../../modules/schedulerApi/appointments/appointment'
    import ZsheetSection from '../../../_common/z/sheet/zsheet-section.svelte'
    import ZsheetField from '../../../_common/z/sheet/zsheet-field.svelte'

    const dispatch = createEventDispatcher()

    export let entity: Appointment

    $: dateValue = entity?.start ? dayjs(entity.start).format('YYYY-MM-DD') : ''
    $: startValue = entity?.start ? dayjs(entity.start).format('HH:mm') : ''
    $: endValue = entity?.end ? dayjs(entity.end).format('HH:mm') : ''
    $: durationMinutes = entity?.start && entity?.end ? Math.round((new Date(entity.end).getTime() - new Date(entity.start).getTime()) / 60000) : 0
    $: serviceMinutes = (entity?.items || []).filter((el) => !!el).reduce((acc, el) => acc + (el.service?.duration || 0), 0)

    function durationLabel(minutes: number) {
        if (minutes <= 0) return '-'
        const h = Math.floor(minutes / 60)
        const m = minutes % 60
        if (h === 0) return `${m} min`
        if (m === 0) return `${h}h`
        return `${h}h${String(m).padStart(2, '0')}`
    }

    function applyDate(value: string) {
        if (!value) return
        const [year, month, day] = value.split('-').map((el) => parseInt(el, 10))
        if (!year || !month || !day) return
        // move o agendamento inteiro de dia: a duração combinada não muda
        const start = new Date(entity.start)
        const end = new Date(entity.end)
        const minutes = Math.round((end.getTime() - start.getTime()) / 60000)
        start.setFullYear(year, month - 1, day)
        entity.start = start
        entity.end = new Date(start.getTime() + minutes * 60000)
        dispatch('change')
    }

    function applyStart(value: string) {
        if (!value) return
        const [hour, minute] = value.split(':').map((el) => parseInt(el, 10))
        if (isNaN(hour) || isNaN(minute)) return
        const start = new Date(entity.start)
        const minutes = durationMinutes
        start.setHours(hour, minute, 0, 0)
        entity.start = start
        // mantém a duração atual; com término automático o pai recalcula pelos serviços
        entity.end = new Date(start.getTime() + Math.max(minutes, 0) * 60000)
        dispatch('change')
    }

    function applyEnd(value: string) {
        if (!value) return
        const [hour, minute] = value.split(':').map((el) => parseInt(el, 10))
        if (isNaN(hour) || isNaN(minute)) return
        const end = new Date(entity.start)
        end.setHours(hour, minute, 0, 0)
        entity.end = end
        dispatch('change')
    }

    function adjust() {
        entity.auto = false
        dispatch('change')
    }

    function backToAuto() {
        entity.auto = true
        dispatch('change')
    }
</script>

<ZsheetSection label="Quando">
    <div class="when">
        <div class="when_row">
            <ZsheetField label="Data">
                <input type="date" value={dateValue} on:change={(ev) => applyDate(ev.currentTarget.value)} />
            </ZsheetField>
            <ZsheetField label="Início">
                <input type="time" value={startValue} on:change={(ev) => applyStart(ev.currentTarget.value)} />
            </ZsheetField>
        </div>

        {#if entity.auto}
            <div class="when_end">
                <div class="when_end_text">
                    Término <strong>{endValue}</strong>
                    <span class="when_end_hint">· {durationLabel(serviceMinutes)} do serviço</span>
                </div>
                <button type="button" class="when_btn" on:click={adjust}>Ajustar</button>
            </div>
        {:else}
            <div class="when_row">
                <ZsheetField label="Término">
                    <input type="time" value={endValue} on:change={(ev) => applyEnd(ev.currentTarget.value)} />
                </ZsheetField>
                <div class="when_auto">
                    <button type="button" class="when_btn" on:click={backToAuto}>Automático</button>
                </div>
            </div>
            <div class="when_end_hint">Duração combinada: {durationLabel(durationMinutes)}</div>
        {/if}
    </div>
</ZsheetSection>

<style>
    /* valores base = tema escuro */
    .when {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }
    /* Grade, e não flex, por causa do input[type=date] do iPhone: ele escreve a data por extenso
       ("8 Oct 2026") e não encolhe abaixo desse texto. No flex, a largura que ele pedia crescia a
       coluna da Data até a do Início sumir — sobrava uma caixa de uns 8px com o horário vazando
       por cima. Numa grade, minmax() limita a trilha por cima (1fr) e por baixo (140px), então
       nenhum conteúdo consegue empurrar a coluna vizinha; e o auto-fit empilha os dois campos
       quando nem 140px cabem, em vez de espremer. */
    .when_row {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
        gap: 10px;
        align-items: end;
    }
    .when_end {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #ffffff08;
        border: 1px solid #ffffff0f;
        border-radius: 10px;
        padding: 8px 10px;
    }
    .when_end_text {
        flex: 1 1 auto;
        font-size: 14px;
    }
    .when_end_hint {
        opacity: 0.55;
        font-size: 12.5px;
    }
    .when_auto {
        flex: 1 1 0;
    }
    .when_btn {
        border: 1px solid #ffffff22;
        background: none;
        color: inherit;
        font: inherit;
        font-size: 13.5px;
        border-radius: 10px;
        padding: 9px 12px;
        min-height: 42px;
        width: 100%;
        white-space: nowrap;
    }
    .when_auto .when_btn {
        /* o campo de término e este botão dividem a linha; sem isso o rótulo cortava */
        font-size: 13px;
        padding: 9px 8px;
    }
    .when_end .when_btn {
        width: auto;
        min-height: 34px;
        padding: 6px 12px;
    }

    :global(.theme_light) .when_end {
        background: #f7f8fa;
        border-color: #e6e8ec;
    }
    :global(.theme_light) .when_btn {
        border-color: #d8dbe0;
    }
</style>
